import { message } from "antd";
import { submitUserImageForRegistration } from "lib/common/endpoints/endUser";
import { Registrations } from "lib/common/types/registration";
import usePost from "lib/hooks/usePost";
import { useEffect, useRef, useState } from "react";
import { UploadSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import Image from "../atomic/Image";
import { ButtonSpinner } from "../atomic/Spinner/Spinner";

interface ImageUpload {
  data: { message: string };
}

export default function RegisterUserImage({
  registration,
  refetch,
  onNext,
}: {
  registration: Registrations;
  refetch: () => {};
  onNext?: () => void;
}) {
  const [files, setFiles] = useState<File>();
  const uploadRef = useRef<HTMLInputElement>(null);
  const {
    data,
    post: uploadImage,
    error,
    isLoading,
  } = usePost<ImageUpload>(
    submitUserImageForRegistration + registration?._id.$oid,
  );
  const [image, setImage] = useState("");
  const handleFileUpload = () => {
    uploadRef.current && uploadRef.current.click();
  };
  useEffect(() => {
    if (error && error.hasOccurred) {
      message.error(error.message);
    }
  }, [error]);
  useEffect(() => {
    if (!error.hasOccurred && image) {
      message.success("Image Uploaded Successfully");
      onNext && onNext();
    }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isLoading]);

  const handleFileChange = (e: any) => {
    e.preventDefault();

    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    setFiles(files[0]);
    const reader = new FileReader();
    reader?.readAsDataURL(files && files[0]);
    reader.onload = () => {
      setImage(reader.result as any);
    };
  };
  if (!registration)
    return (
      <div className="text-center text-sm text-gray-500 dark:text-gray-400">
        You need to fill up the registration form first.
      </div>
    );

  return (
    <section className="max-w-sm mx-auto text-center space-y-1">
      <h4 className="text-base font-bold text-gray-900 dark:text-gray-100 m-0">
        Upload your photo
      </h4>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        A clear, full-faced photo. Used by Clamphook only — never shared with
        third parties.
      </p>

      {registration.userImage && (
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Your previously submitted photo — reupload to change it.
          </p>
          <Image
            src={registration.userImage}
            width={112}
            height={112}
            alt="Previously submitted photo"
            className="rounded-full mx-auto border border-gray-200 dark:border-gray-700"
          />
        </div>
      )}

      {image && (
        <div className="mt-4 flex flex-col items-center gap-3 rounded-xl border border-primary-100 dark:border-primary-900/40 bg-primary-50 dark:bg-primary-900/20 p-4">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={image}
            className="w-24 h-24 rounded-full object-cover border-2 border-white dark:border-gray-800 shadow-sm"
            alt="preview"
          />
          <p className="text-xs font-semibold text-primary dark:text-primary-300">Preview</p>
          <button
            type="button"
            onClick={() => {
              const formData = new FormData();
              formData.append("file", files as Blob);
              uploadImage(formData);
            }}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary hover:bg-primary-600 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <PaperPlaneTilt size={14} weight="fill" />
            Submit
            {isLoading && <ButtonSpinner />}
          </button>
        </div>
      )}

      <input
        ref={uploadRef}
        className="opacity-0 h-0 w-0"
        type="file"
        name="image"
        accept="image/png, image/jpeg"
        id="image"
        onChange={handleFileChange}
      />
      <button
        type="button"
        onClick={handleFileUpload}
        className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold transition-colors"
      >
        <UploadSimple size={15} />
        Upload photo
      </button>
    </section>
  );
}
